/**
 * Fish Audio API Service
 * 文档: https://docs.fish.audio/api-reference
 */

const DEFAULT_BASE_URL = '/api/fish-audio'

export interface FishAudioModel {
  _id: string
  type: string
  title: string
  description: string
  cover_image: string
  train_mode: string
  state: 'created' | 'training' | 'trained' | 'failed'
  tags: string[]
  samples: unknown[]
  created_at: string
  updated_at: string
  languages: string[]
  visibility: 'public' | 'unlist' | 'private'
  like_count: number
  mark_count: number
  shared_count: number
  task_count: number
  author: {
    _id: string
    nickname: string
    avatar: string
  }
}

export interface CreateModelParams {
  file: File
  title: string
  apiKey: string
  baseUrl?: string
  trainMode?: string
  visibility?: string
  enhanceAudioQuality?: boolean
}

export interface GenerateSpeechParams {
  text: string
  referenceId: string
  apiKey: string
  baseUrl?: string
  model?: string
  format?: string
  temperature?: number
  topP?: number
  speed?: number
}

function resolveBaseUrl(baseUrl?: string): string {
  const trimmed = baseUrl?.trim().replace(/\/+$/, '')
  return trimmed || DEFAULT_BASE_URL
}

/**
 * 创建语音模型（语音克隆）
 * 上传音频文件，Fish Audio 会训练一个 TTS 模型
 */
export async function createModel(params: CreateModelParams): Promise<{ modelId: string, title: string }> {
  const {
    file,
    title,
    apiKey,
    baseUrl,
    trainMode = 'fast',
    visibility = 'private',
    enhanceAudioQuality = true,
  } = params

  if (file.size > 50 * 1024 * 1024) {
    throw new Error('文件大小不能超过 50MB')
  }

  const formData = new FormData()
  formData.append('type', 'tts')
  formData.append('title', title)
  formData.append('train_mode', trainMode)
  formData.append('visibility', visibility)
  formData.append('enhance_audio_quality', String(enhanceAudioQuality))
  formData.append('voices', file)

  const url = `${resolveBaseUrl(baseUrl)}/model`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${apiKey}`,
    },
    body: formData,
  })

  if (!response.ok) {
    const errorText = await response.text()
    let errorData: any = {}
    try {
      errorData = JSON.parse(errorText)
    }
    catch {}

    if (response.status === 401)
      throw new Error('API Key 无效，请检查后重试')
    if (response.status === 422)
      throw new Error(`参数错误: ${errorData?.[0]?.msg || errorText}`)
    throw new Error(errorData.message || `创建模型失败: ${response.status}`)
  }

  const data = await response.json()
  return {
    modelId: data._id,
    title: data.title,
  }
}

/**
 * 列出用户的语音模型
 */
export async function listModels(params: {
  apiKey: string
  baseUrl?: string
  pageSize?: number
  pageNumber?: number
}): Promise<{ total: number, items: FishAudioModel[], has_more?: boolean }> {
  const { apiKey, baseUrl, pageSize = 50, pageNumber = 1 } = params

  // 使用字符串拼接而非 new URL()，避免相对路径（如 /api/fish-audio）无 base 时抛 TypeError
  const query = new URLSearchParams({
    page_size: String(pageSize),
    page_number: String(pageNumber),
    self: 'true',
  })
  const endpoint = `${resolveBaseUrl(baseUrl)}/model?${query.toString()}`

  const response = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${apiKey}`,
    },
  })

  if (!response.ok) {
    if (response.status === 401)
      throw new Error('API Key 无效')
    throw new Error(`获取模型列表失败: ${response.status}`)
  }

  return await response.json()
}

/**
 * 获取单个语音模型详情（用于手动填入 model_id 时校验）
 */
export async function getModel(params: {
  modelId: string
  apiKey: string
  baseUrl?: string
}): Promise<FishAudioModel> {
  const { modelId, apiKey, baseUrl } = params

  const response = await fetch(`${resolveBaseUrl(baseUrl)}/model/${modelId}`, {
    headers: { Authorization: `Bearer ${apiKey}` },
  })

  if (!response.ok) {
    if (response.status === 401)
      throw new Error('API Key 无效')
    if (response.status === 404)
      throw new Error('找不到该模型，请确认 model_id 是否正确')
    throw new Error(`获取模型失败: ${response.status}`)
  }

  return await response.json()
}

/**
 * 删除语音模型
 */
export async function deleteModel(params: {
  modelId: string
  apiKey: string
  baseUrl?: string
}): Promise<void> {
  const { modelId, apiKey, baseUrl } = params

  const response = await fetch(`${resolveBaseUrl(baseUrl)}/model/${modelId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${apiKey}`,
    },
  })

  if (!response.ok) {
    if (response.status === 401)
      throw new Error('API Key 无效或无权删除此模型')
    const err = new Error(`删除模型失败: ${response.status}`)
    ;(err as any).status = response.status
    throw err
  }
}

/**
 * 语音合成（TTS）
 * 使用 reference_id 指定克隆的声音模型
 */
export async function generateSpeech(params: GenerateSpeechParams): Promise<ArrayBuffer> {
  const {
    text,
    referenceId,
    apiKey,
    baseUrl,
    model = 's2.1-pro',
    format = 'mp3',
    temperature = 0.7,
    topP = 0.7,
    speed = 1,
  } = params

  if (!text?.trim())
    throw new Error('文本内容为空')
  if (!referenceId)
    throw new Error('未指定语音模型')

  const body: Record<string, unknown> = {
    text: text.trim(),
    reference_id: referenceId,
    format,
    temperature,
    top_p: topP,
  }

  if (speed !== 1) {
    body.prosody = { speed }
  }

  const url = `${resolveBaseUrl(baseUrl)}/v1/tts`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
      'model': model,
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const errorText = await response.text()
    let errorData: any = {}
    try {
      errorData = JSON.parse(errorText)
    }
    catch {}

    if (response.status === 401)
      throw new Error('API Key 无效')
    if (response.status === 402)
      throw new Error('余额不足，请充值后再试')
    if (response.status === 422)
      throw new Error(`参数错误: ${errorData?.[0]?.msg || errorText}`)
    throw new Error(errorData.message || `语音合成失败: ${response.status}`)
  }

  return await response.arrayBuffer()
}
